import React, { useMemo, useState } from "react"
import { Collapse, Input } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCopy } from "@fortawesome/free-solid-svg-icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import {
  ExportMapFillResult,
  ExportMapFillUnmatchedReason,
  ExportMapFillWarningKind,
} from "types/sourceCodes.types"

/**
 * Counts of what Apply will change, computed against the current store
 * (so they honour "only fill rows without a code").
 */
export interface ExportMapFillPreviewCounts {
  variableCount: number
  categoryCount: number
  /** Proposed rows whose label is not in the current export map */
  unknownLabels: string[]
}

interface ExportMapSourceFillReviewProps {
  result: ExportMapFillResult
  preview: ExportMapFillPreviewCounts
  onApply: () => void
  onCancel: () => void
  /** One line about what this run did with the project's stored source codes */
  storageNote?: string
  /** One line about the extraction that produced the codes being matched */
  extractionWarning?: string
}

const UNMATCHED_REASON_LABELS: Record<ExportMapFillUnmatchedReason, string> = {
  "no-match": "No matching question in the source document",
  "low-confidence": "Match below the confidence threshold",
  "invalid-reference": "Matched a reference that is not in the source",
}

const WARNING_KIND_LABELS: Record<ExportMapFillWarningKind, string> = {
  "duplicate-variable": "Duplicate variable names",
  "invalid-variable-name": "Invalid variable names",
  "non-numeric-code": "Non-numeric category codes",
  "category-not-in-source": "Categories not in the source document",
  "chunk-failed": "Chunk failed",
  "list-unresolved": "List unresolved",
}

/**
 * Shown when the service could not process part of the map: those rows carry no
 * proposal at all, which otherwise looks the same as "no code was found".
 */
const FAILED_CHUNKS_NOTICE =
  "Some parts of the map could not be processed; rerun to try again."

const UNKNOWN_LABEL_GROUP = "Proposed for a row that is not in this export map"

interface ReviewLine {
  label: string
  detail?: string
}

interface ReviewGroup {
  key: string
  title: string
  lines: ReviewLine[]
}

const copyLines = (lines: ReviewLine[]): void => {
  void navigator.clipboard?.writeText(
    lines
      .map((line) => (line.detail ? `${line.label}\t${line.detail}` : line.label))
      .join("\n")
  )
}

/**
 * One titled, bounded, mono list with a copy button (UnmappedColumnsModal style).
 */
const ReviewGroupList: React.FC<{ group: ReviewGroup }> = ({ group }) => (
  <div className="mb-4 last:mb-0">
    <div className="flex items-center justify-between mb-1">
      <h4 className="text-sm font-medium text-slate-800 m-0">
        {group.title} ({group.lines.length})
      </h4>
      <button
        type="button"
        onClick={() => copyLines(group.lines)}
        aria-label={`Copy ${group.title}`}
        className="text-slate-500 hover:text-slate-800 transition-colors p-1"
      >
        <FontAwesomeIcon icon={faCopy} />
      </button>
    </div>
    <div className="border border-slate-200 rounded overflow-hidden">
      <div className="max-h-40 overflow-y-auto overflow-x-hidden [scrollbar-width:thin]">
        {group.lines.map((line, i) => (
          <div
            key={`${line.label}-${i}`}
            className="px-3 py-1.5 text-sm border-b border-slate-100 last:border-b-0 min-w-0"
          >
            <span className="font-mono text-slate-800 break-all">{line.label}</span>
            {line.detail && (
              <span className="block font-mono text-xs text-slate-500 break-all">
                {line.detail}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
)

const filterGroups = (groups: ReviewGroup[], query: string): ReviewGroup[] => {
  if (query === "") return groups
  return groups
    .map((group) => ({
      ...group,
      lines: group.lines.filter(
        (line) =>
          line.label.toLowerCase().includes(query) ||
          (line.detail ?? "").toLowerCase().includes(query)
      ),
    }))
    .filter((group) => group.lines.length > 0)
}

/**
 * Review step of "Fill from source document": what will be filled, which rows
 * found no source code (grouped by reason) and what the matcher warned about
 * (grouped by kind). Both lists are searchable and copyable.
 *
 * @component
 */
const ExportMapSourceFillReview: React.FC<ExportMapSourceFillReviewProps> = ({
  result,
  preview,
  onApply,
  onCancel,
  storageNote,
  extractionWarning,
}) => {
  const [search, setSearch] = useState("")
  const query = search.trim().toLowerCase()

  const unmatchedGroups = useMemo((): ReviewGroup[] => {
    const byReason = new Map<string, ReviewLine[]>()
    for (const entry of result.unmatched) {
      const lines = byReason.get(entry.reason) ?? []
      lines.push({ label: entry.fullLabel })
      byReason.set(entry.reason, lines)
    }
    const groups: ReviewGroup[] = []
    for (const reason of Object.keys(
      UNMATCHED_REASON_LABELS
    ) as ExportMapFillUnmatchedReason[]) {
      const lines = byReason.get(reason)
      if (lines) {
        groups.push({ key: reason, title: UNMATCHED_REASON_LABELS[reason], lines })
      }
    }
    if (preview.unknownLabels.length > 0) {
      groups.push({
        key: "unknown-label",
        title: UNKNOWN_LABEL_GROUP,
        lines: preview.unknownLabels.map((label) => ({ label })),
      })
    }
    return groups
  }, [result.unmatched, preview.unknownLabels])

  const warningGroups = useMemo((): ReviewGroup[] => {
    const byKind = new Map<string, ReviewLine[]>()
    for (const warning of result.warnings) {
      const lines = byKind.get(warning.kind) ?? []
      lines.push({ label: warning.fullLabel, detail: warning.detail })
      byKind.set(warning.kind, lines)
    }
    const groups: ReviewGroup[] = []
    for (const kind of Object.keys(
      WARNING_KIND_LABELS
    ) as ExportMapFillWarningKind[]) {
      const lines = byKind.get(kind)
      if (lines) {
        groups.push({ key: kind, title: WARNING_KIND_LABELS[kind], lines })
      }
    }
    return groups
  }, [result.warnings])

  const visibleUnmatched = useMemo(
    () => filterGroups(unmatchedGroups, query),
    [unmatchedGroups, query]
  )
  const visibleWarnings = useMemo(
    () => filterGroups(warningGroups, query),
    [warningGroups, query]
  )

  const unmatchedTotal = result.unmatched.length + preview.unknownLabels.length
  const warningTotal = result.warnings.length
  const failedChunks = result.stats.failedChunks ?? 0
  const nothingToApply = preview.variableCount === 0 && preview.categoryCount === 0

  const collapseItems = [
    {
      key: "unmatched",
      label: `Unmatched rows (${unmatchedTotal})`,
      children:
        visibleUnmatched.length > 0 ? (
          visibleUnmatched.map((group) => (
            <ReviewGroupList key={group.key} group={group} />
          ))
        ) : (
          <p className="text-sm text-slate-500 m-0">
            {unmatchedTotal === 0
              ? "Every row matched a source code."
              : `No unmatched rows match "${search}".`}
          </p>
        ),
    },
    {
      key: "warnings",
      label: `Warnings (${warningTotal})`,
      children:
        visibleWarnings.length > 0 ? (
          visibleWarnings.map((group) => (
            <ReviewGroupList key={group.key} group={group} />
          ))
        ) : (
          <p className="text-sm text-slate-500 m-0">
            {warningTotal === 0
              ? "The matcher raised no warnings."
              : `No warnings match "${search}".`}
          </p>
        ),
    },
  ]

  return (
    <div className="space-y-4">
      <div className="text-sm text-slate-700" data-testid="fill-stats">
        <strong>{result.stats.matched}</strong> of{" "}
        <strong>{result.stats.rows}</strong> rows matched. Apply will fill{" "}
        <strong>{preview.variableCount}</strong>{" "}
        {preview.variableCount === 1 ? "variable" : "variables"} and{" "}
        <strong>{preview.categoryCount}</strong>{" "}
        {preview.categoryCount === 1 ? "category" : "categories"}.{" "}
        <strong>{unmatchedTotal}</strong> unmatched.
      </div>

      {extractionWarning && (
        <p className="text-sm text-red-600 m-0" data-testid="extraction-warning">
          {extractionWarning}
        </p>
      )}

      {storageNote && (
        <p className="text-sm text-slate-600 m-0" data-testid="storage-note">
          {storageNote}
        </p>
      )}

      {failedChunks > 0 && (
        <p className="text-sm text-amber-700 m-0" data-testid="failed-chunks">
          {FAILED_CHUNKS_NOTICE}
        </p>
      )}

      {nothingToApply && (
        <p className="text-sm text-amber-700 m-0">
          Nothing would change: the proposed codes already match the export map, or
          every proposed row already has a code.
        </p>
      )}

      <Input.Search
        allowClear
        placeholder="Search labels and details"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <Collapse
        items={collapseItems}
        defaultActiveKey={[
          ...(unmatchedTotal > 0 ? ["unmatched"] : []),
          ...(warningTotal > 0 ? ["warnings"] : []),
        ]}
      />

      <div className="flex flex-row space-x-2 pt-2">
        <DataQButton className="ml-auto" type="secondary" onClick={onCancel}>
          Cancel
        </DataQButton>
        <DataQButton type="primary" onClick={onApply} isDisabled={nothingToApply}>
          Apply
        </DataQButton>
      </div>
    </div>
  )
}

export default ExportMapSourceFillReview
